/**
 * getPlacePresentation — Central presentation helper for PlaceCore public v2.1.
 * Normalizes place_core data with robust fallbacks. No AI calls.
 */
import type { FarmlyPlace } from "@/lib/types";
import { resolveRegion } from "@/lib/seo/placeSeo";
import { buildVibeText } from "@/lib/placeCore/derivePlaceSignals";
import {
  normalizeSeasons,
  formatIntent,
  tagsToIntents,
  INTENT_CONFIG,
} from "./mappings";

export interface PlaceContact {
  website?: string;
  instagram?: string;
  phone?: string;
  email?: string;
}

export interface PlacePresentation {
  title: string;
  subtitle: string;
  verified: boolean;
  imageUrl: string | null;
  badges: (
    | { type: "season"; seasons: string[] }
    | { type: "duration"; duration: string }
  )[];
  vibeText: string | null;
  intents: { key: string; label: string }[];
  tagChips: string[];
  factsRows: { label: string; value: string }[];
  didYouKnow: string | null;
  lat: number | null;
  lng: number | null;
  mapTitle: string;
  mapSubtitle: string | null;
  regionLabel: string;
  contact: PlaceContact;
}

export function getPlacePresentation(place: FarmlyPlace): PlacePresentation {
  const pc = (place.place_core ?? {}) as Record<string, any>;
  const regionLabel = resolveRegion(place);

  const subtitle = `plats i ${regionLabel}`;

  // --- Verified ---
  const verified = Boolean(place.verified_at);

  // --- Badges (max 2: season + duration) ---
  const badges: PlacePresentation["badges"] = [];

  const seasons = normalizeSeasons(pc);
  if (seasons.length > 0) {
    badges.push({ type: "season", seasons });
  }

  if (pc.duration) {
    badges.push({ type: "duration", duration: pc.duration });
  }

  // --- Vibe text (fallback chain) ---
  let vibeText: string | null = null;
  if (typeof pc.vibe === "string" && pc.vibe.length >= 80) {
    vibeText = pc.vibe;
  } else if (place.description_short && place.description_short.length >= 80) {
    vibeText = place.description_short;
  } else {
    const fallback = buildVibeText(place);
    if (fallback.length >= 80) vibeText = fallback;
  }

  // --- Intent chips (from place_core.intents, synced from place_intents via trigger) ---
  const intentsRaw: string[] = pc.intents ?? [];
  let intentsKeys: string[];
  if (intentsRaw.length > 0) {
    intentsKeys = intentsRaw.filter((i) => i in INTENT_CONFIG).slice(0, 6);
  } else {
    // Fallback: derive from legacy tags
    intentsKeys = tagsToIntents(place.tags).slice(0, 3);
  }
  const intents = intentsKeys.map((key) => ({
    key,
    label: formatIntent(key),
  }));

  // --- Tag chips (max 6, deduped) ---
  const tagChips = [...new Set(place.tags ?? [])].slice(0, 6);

  // --- Facts rows (ordered) ---
  const factsRows: { label: string; value: string }[] = [];
  const area = typeof pc.area === "string" && pc.area.length > 0 ? pc.area : null;
  if (area) factsRows.push({ label: "place.facts.area", value: area });
  if (place.municipality) factsRows.push({ label: "place.facts.municipality", value: place.municipality });
  if (place.landskap) factsRows.push({ label: "place.facts.landskap", value: place.landskap });
  if (place.county && place.county !== place.landskap) {
    factsRows.push({ label: "place.facts.county", value: place.county });
  }
  if (place.address_text) factsRows.push({ label: "place.facts.address", value: place.address_text });

  // --- Did you know ---
  const didYouKnow =
    typeof pc.did_you_know === "string" && pc.did_you_know.length >= 10
      ? pc.did_you_know
      : null;

  // --- Map ---
  const mapTitle = place.address_text || place.municipality || place.name;
  const mapSubtitle = place.landskap ?? null;

  // --- Contact (prefer contact_* fields, fall back to legacy) ---
  const contact: PlaceContact = {};
  const cw = place.contact_website || place.website;
  const ci = place.contact_instagram || place.instagram;
  const cp = place.contact_phone || place.phone;
  if (cw) contact.website = cw;
  if (ci) contact.instagram = ci;
  if (cp) contact.phone = cp;
  if (place.contact_email) contact.email = place.contact_email;

  return {
    title: place.name,
    subtitle,
    verified,
    imageUrl: null, // Resolved from media_assets
    badges: badges.slice(0, 2),
    vibeText,
    intents,
    tagChips,
    factsRows,
    didYouKnow,
    lat: place.lat ?? null,
    lng: place.lng ?? null,
    mapTitle,
    mapSubtitle,
    regionLabel,
    contact,
  };
}

/** Feature gate: determines if PlaceCorePublicView should be used */
export function shouldUsePlaceCore(place: FarmlyPlace): boolean {
  if (place.slug === "lisskog") return true;
  if (place.slug.startsWith("e2e-place-")) return true;
  const pc = (place.place_core ?? {}) as Record<string, any>;
  return typeof pc.vibe === "string" && pc.vibe.length >= 80;
}
